import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Search, ShoppingBag, Sprout } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useProducts } from "../hooks/useProducts";
import { SITE, formatPrice } from "../config/site";

export const NAV_LINKS = [
  { name: "Home", href: "/" },
  { name: "Shop", href: "/shop" },
  { name: "Trattamento", href: "/prodotto/trattamento-rapida-crescita" },
  { name: "Scrub", href: "/scrub" },
  { name: "Federa in Seta", href: "/prodotto/fibra-di-seta" },
  { name: "Percorsi", href: "/kit" },
  { name: "Quiz", href: "/quiz" },
  { name: "Recensioni", href: "/recensioni" },
  { name: "FAQ", href: "/faq" },
  { name: "Contatti", href: "/contatti" },
];

const SearchOverlay = ({ onClose }) => {
  const { products } = useProducts();
  const [query, setQuery] = useState("");
  const navigate = useNavigate();
  const results = products.filter((p) =>
    p.name.toLowerCase().includes(query.toLowerCase())
  );

  return (
    <motion.div
      data-testid="search-overlay"
      className="fixed inset-0 z-[70] bg-lilac-soft/95 backdrop-blur-xl flex flex-col"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <div className="max-w-2xl w-full mx-auto px-6 pt-24">
        <div className="flex items-center gap-4 border-b-2 border-plum/20 pb-4">
          <Search className="w-6 h-6 text-plum" />
          <input
            data-testid="search-input"
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Cerca un prodotto..."
            className="flex-1 bg-transparent outline-none font-serif text-2xl sm:text-3xl text-plum placeholder:text-plum/30"
          />
          <button data-testid="search-close-btn" onClick={onClose} className="p-2 text-plum hover:rotate-90 transition-transform duration-300">
            <X className="w-6 h-6" />
          </button>
        </div>
        <div className="mt-8 space-y-3 overflow-y-auto max-h-[60vh] pb-10">
          {results.map((p) => (
            <button
              key={p.slug}
              data-testid={`search-result-${p.slug}`}
              onClick={() => {
                onClose();
                navigate(`/prodotto/${p.slug}`);
              }}
              className="w-full flex items-center gap-4 bg-white rounded-2xl p-3 text-left hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300"
            >
              <img src={p.image} alt={p.name} className="w-14 h-14 rounded-xl object-cover" />
              <div className="flex-1">
                <p className="font-medium text-plum text-sm">{p.name}</p>
                <p className="text-xs text-plum/50">{p.format}</p>
              </div>
              <span className="font-serif text-plum">{formatPrice(p.price)}</span>
            </button>
          ))}
          {results.length === 0 && (
            <p className="text-plum/50 text-sm pt-4">Nessun prodotto trovato per “{query}”.</p>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const { count, setOpen } = useCart();

  return (
    <>
      <header className="sticky top-0 z-50 bg-white/75 backdrop-blur-xl border-b border-lilac-light/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between h-16">
          <button
            data-testid="mobile-menu-btn"
            onClick={() => setMenuOpen(true)}
            className="lg:hidden p-2 -ml-2 text-plum"
            aria-label="Apri menu"
          >
            <Menu className="w-6 h-6" />
          </button>

          <Link to="/" data-testid="brand-logo" className="flex items-center gap-2 group">
            <span className="w-8 h-8 rounded-full bg-lilac flex items-center justify-center group-hover:rotate-12 transition-transform duration-300">
              <Sprout className="w-4 h-4 text-white" />
            </span>
            <span className="font-serif text-xl sm:text-2xl text-plum tracking-tight">
              {SITE.brand}
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-5" data-testid="desktop-nav">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.href}
                to={link.href}
                data-testid={`nav-${link.name.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `text-[11px] font-semibold uppercase tracking-[0.14em] transition-colors duration-300 ${
                    isActive ? "text-lilac-dark" : "text-plum/70 hover:text-plum"
                  }`
                }
              >
                {link.name}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-1 sm:gap-2">
            <button
              data-testid="search-open-btn"
              onClick={() => setSearchOpen(true)}
              className="p-2 text-plum hover:bg-lilac-soft rounded-full transition-colors"
              aria-label="Cerca"
            >
              <Search className="w-5 h-5" />
            </button>
            <button
              data-testid="cart-open-btn"
              onClick={() => setOpen(true)}
              className="relative p-2 text-plum hover:bg-lilac-soft rounded-full transition-colors"
              aria-label="Carrello"
            >
              <ShoppingBag className="w-5 h-5" />
              {count > 0 && (
                <span
                  data-testid="cart-count-badge"
                  className="absolute -top-0.5 -right-0.5 w-5 h-5 rounded-full bg-lilac text-white text-[10px] font-bold flex items-center justify-center"
                >
                  {count}
                </span>
              )}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            data-testid="mobile-menu"
            className="fixed inset-0 z-[60] lg:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="absolute inset-0 bg-plum/30 backdrop-blur-sm" onClick={() => setMenuOpen(false)} />
            <motion.div
              className="absolute left-0 top-0 bottom-0 w-[82%] max-w-sm bg-white shadow-2xl flex flex-col"
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ type: "spring", damping: 28, stiffness: 260 }}
            >
              <div className="flex items-center justify-between p-5 border-b border-lilac-light">
                <span className="font-serif text-xl text-plum">{SITE.brand}</span>
                <button data-testid="mobile-menu-close-btn" onClick={() => setMenuOpen(false)} className="p-2 text-plum" aria-label="Chiudi menu">
                  <X className="w-6 h-6" />
                </button>
              </div>
              <nav className="flex-1 overflow-y-auto p-5 space-y-1">
                {NAV_LINKS.map((link, i) => (
                  <motion.div
                    key={link.href}
                    initial={{ opacity: 0, x: -16 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.05 + i * 0.04 }}
                  >
                    <NavLink
                      to={link.href}
                      data-testid={`mobile-nav-${link.name.toLowerCase().replace(/\s+/g, "-")}`}
                      onClick={() => setMenuOpen(false)}
                      className="block py-3 px-4 rounded-xl font-serif text-lg text-plum hover:bg-lilac-soft transition-colors"
                    >
                      {link.name}
                    </NavLink>
                  </motion.div>
                ))}
              </nav>
              <div className="p-5 border-t border-lilac-light">
                <p className="text-xs text-plum/50 text-center">{SITE.website}</p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>{searchOpen && <SearchOverlay onClose={() => setSearchOpen(false)} />}</AnimatePresence>
    </>
  );
};
